import { EmptyState } from '@/components/empty-state';
import { PageHeader } from '@/components/shell/page-header';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/cn';
import { useCampaigns, useContextTree, useCreateCampaign, useExperiences, type Campaign } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/campaigns/')({
  validateSearch: (search: Record<string, unknown>): { experience?: string } =>
    typeof search.experience === 'string' ? { experience: search.experience } : {},
  component: CampaignsList,
});

/** Design hand-off §3. Tailwind can't build a class name from a variable, so the map is literal. */
const STATUS_CLASS: Record<Campaign['status'], string> = {
  draft: 'border-zinc-500/30 bg-zinc-500/10 text-zinc-600 dark:text-zinc-300',
  active: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  paused: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  archived: 'border-zinc-400/30 bg-transparent text-zinc-400',
};

export function StatusBadge({ status }: { status: Campaign['status'] }): React.JSX.Element {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium capitalize',
        STATUS_CLASS[status]
      )}
    >
      {status}
    </span>
  );
}

const PRIORITY_LABEL: Record<Campaign['priority'], string> = {
  active: 'Active',
  passive: 'Passive',
  time_sensitive: 'Time-sensitive',
};

function CampaignsList(): React.JSX.Element {
  const { experience } = Route.useSearch();
  const { appId, environmentId } = useContextStore();
  const { data: campaigns, isLoading } = useCampaigns(appId);

  // The list hook is app-scoped; environment is the publish target, so a campaign from another
  // environment is not this page's business.
  const rows = campaigns?.filter((c) => c.environment_id === environmentId) ?? [];
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | Campaign['status']>('all');
  const visible = rows.filter(
    (campaign) =>
      campaign.name.toLowerCase().includes(query.toLowerCase()) && (status === 'all' || campaign.status === status)
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Campaigns"
        description="Control who sees each experience, when it appears, and what is live in this environment."
        actions={<NewCampaignDialog initialExperienceId={experience} autoOpen={Boolean(experience)} />}
      />

      {isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No campaigns in this environment"
          description="A campaign points a segment at an experience and decides when it fires. Create one, link it, publish it."
          action={<NewCampaignDialog />}
        />
      ) : (
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative max-w-sm flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search campaigns" className="pl-9" />
            </div>
            <Select value={status} onValueChange={(value) => setStatus(value as typeof status)}>
              <SelectTrigger className="w-full sm:w-40" aria-label="Filter by status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Priority</TableHead>
                <TableHead className="text-right">Bundle version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell className="font-medium">
                    <Link
                      to="/campaigns/$id"
                      params={{ id: campaign.id }}
                      className="block hover:underline"
                    >
                      {campaign.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <StatusBadge status={campaign.status} />
                  </TableCell>
                  <TableCell className="text-muted-foreground">{PRIORITY_LABEL[campaign.priority]}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">
                    {campaign.published_bundle_version ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {visible.length === 0 ? <p className="border-t px-4 py-10 text-center text-sm text-muted-foreground">No campaigns match these filters.</p> : null}
          </div>
        </div>
      )}
    </div>
  );
}

const NONE = '__none__';

export function NewCampaignDialog({
  compact = false,
  initialExperienceId,
  autoOpen = false,
}: {
  compact?: boolean;
  initialExperienceId?: string | undefined;
  autoOpen?: boolean;
}): React.JSX.Element {
  const navigate = useNavigate();
  const { appId, environmentId } = useContextStore();
  const { data: orgs } = useContextTree();
  const { data: experiences } = useExperiences(appId);
  const create = useCreateCampaign(appId);

  const [open, setOpen] = useState(autoOpen);
  const [name, setName] = useState('');
  const [envId, setEnvId] = useState(environmentId ?? '');
  const [experienceId, setExperienceId] = useState(initialExperienceId ?? NONE);

  const environments =
    orgs
      ?.flatMap((o) => o.workspaces)
      .flatMap((w) => w.apps)
      .find((a) => a.id === appId)?.environments ?? [];

  function submit(): void {
    create.mutate(
      {
        name: name.trim(),
        environment_id: envId,
        experience_id: experienceId === NONE ? null : experienceId,
      },
      {
        onSuccess: (campaign) => {
          setOpen(false);
          setName('');
          void navigate({ to: '/campaigns/$id', params: { id: campaign.id } });
        },
        onError: (error: Error) => toast.error(error.message),
      }
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          setEnvId(environmentId ?? '');
          setExperienceId(initialExperienceId ?? NONE);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size={compact ? 'sm' : 'default'}>
          <Plus className="size-4" />
          New campaign
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New campaign</DialogTitle>
          <DialogDescription>
            {initialExperienceId
              ? 'It starts as a draft with the current experience preselected. Configure delivery on the next screen.'
              : 'It starts as a draft. Choose the experience and configure delivery on the next screen.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="campaign-name">Name</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Onboarding nudge"
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="campaign-experience">Experience</Label>
            <Select value={experienceId} onValueChange={setExperienceId}>
              <SelectTrigger id="campaign-experience">
                <SelectValue placeholder="Select an experience" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not linked</SelectItem>
                {experiences?.map((experience) => (
                  <SelectItem key={experience.id} value={experience.id}>
                    {experience.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {initialExperienceId && experienceId === initialExperienceId ? (
              <p className="text-xs text-emerald-700 dark:text-emerald-400">Preselected from the experience builder.</p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="campaign-env">Environment</Label>
            <Select value={envId} onValueChange={setEnvId}>
              <SelectTrigger id="campaign-env">
                <SelectValue placeholder="Select an environment" />
              </SelectTrigger>
              <SelectContent>
                {environments.map((env) => (
                  <SelectItem key={env.id} value={env.id} className="capitalize">
                    {env.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!name.trim() || !envId || create.isPending}>
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
